import { config } from "@config";
import { AssetFilterObject } from "@modules/staking/domain/infrastructure/staking.remote";
import { AssetOrderingOptions, Order } from "@gql/staking";
import { Dictionary } from "@helpers/types";

// Trading
export const GLOBAL_MIN_TRADE_AMOUNT_IN_EUR: number = 1;
export const PORTFOLIO_ZERO_VALUE_FOR_STRINGS_NUMBERS: string = "0.00";

// Config
export const DRY_RUN: boolean = config.dryRun;
export const API_URL: string = config.apiUrl;
export const API_VERSION: string = config.apiVersion;
export const ACCESS_TOKEN_KEY: string = "access-token";
export const PSP_REDIRECTION: string = "fiat-wallet/:pid/transaction/:fpid/:action";
export const MT_QUERY_PARAMS: string = "mtBuyQueryParams";

// 2FA
export const TWO_FA_ENABLING_STEPS: number = 1;
export const TWO_FA_DISABLING_STEPS: number = 3;
export const VERIFICATION_PIN_LENGTH: number = 4;
export const NEW_VERIFICATION_PIN_LENGTH: number = 6;

// Crypto, Asset
export const TOKEN_BEST_ID: string = "33";
export const ELIGIBLE_ICO_COINS: string[] = ["eth"]; // Add new ICO warning coins in this list in the future
export const MAX_CHARACTERS_FOR_DISPLAY: number = 17;
export const ERC20_TOKEN_FAMILY: string = "ETH";
export const DEFAULT_LOGO_URL: string = "/assets/images/default-asset.svg";
export const PRECISION_FOR_TRANSACTION: number = 8;
export const STEELCOIN_INTERNAL_ID: string = "4418";

export const BEST_SYMBOL: string = "best";

// Fiat
export const FIAT_EURO_ID: string = "1";
export const FIAT_DOLLAR_ID: string = "2";
export const FIAT_GBP_ID: string = "4";

// BEST general
export const BEST_VIP_LEVELS_COUNT = 5; // total amount of vip levels
export const BEST_VIP_LEVEL_1_MIN_AMOUNT = 10; // we don't get it via the endpoint when VIP level = 0
export const BEST_REWARDS_MAX_PERCENT_PRECISION = 3; // how many decimals the percentages can display

// BEST fee collection
export const BFC_MIN_AMOUNT: number = 0.1;

// BEST Progress circle
export const VIP_LEVEL_PROGRESS_BAR_RADIUS: number = 27; // Account page progress bar

// BEST 2.0
export const BEST_TWO_TERMS_ON_HOLD: string = "is_accepting_best2_terms_later";

// BEST Staking benefits
export const BEST_STAKING_LEVEL_BENEFITS: number[] = [2.5, 5, 7.5, 10, 12.5];

// List defaults
export const SAVINGS_WIDGET_ITEM_LIMIT: number = 8;

// Index
export const VISIBLE_LIST_ITEMS: number = 5;

// Infinite Scroll
export const INFINITE_SEARCH_DELAY: number = 250; // 250ms debounce time for search input
export const INFINITE_MIN_DISPLAY_ITEMS: number = 12; // Number of items necessary for infinite scroll to kick in

//Leverage
export const LEVERAGED_ASSETS_RISKS_ACCEPTED: string = "leveraged_assets_risks_status";

// Kyc-Fma
export const SECONDS_IN_DAYS: number = 86400000; // Number of seconds in one day

// Add-Files
export const ALLOWED_FILE_TYPE: string[] = [".pdf", ".jpeg", ".jpg", ".png"];
export const MAX_ALLOWED_FILE_SIZE: number = 25000000;
export const MAX_ALLOWED_FILE_AMOUNT: number = 30;

// Notifications
export const NOTIFICATIONS_PER_PAGE: number = 10;

// Break point
export const BREAK_POINT_SCREEN_MD: string = "(min-width: 768px)";

// STAKING
export enum STAKE_TYPE {
	STAKE = "STAKE",
	UNSTAKE = "UNSTAKE",
	REWARD = "REWARD",
	OVERVIEW = "OVERVIEW"
}

export const DEFAULT_STAKEABLE_FILTER: AssetFilterObject = {
	option: AssetOrderingOptions.YearlyInterest,
	order: Order.Desc
};

//
export const COIN_INFO_LIMIT: number = 165;

// Style
export const DEFAULT_SCROLL_OFFSET: number = 20; // If the navbar is not rendered, we are using this value
export const HOLDINGS_DONUT_RADIUS: number = 15.91549430918954; // Circle magic for the holdings-donut
export const HOLDINGS_DONUT_NORTH_POSITION: number = 25; // North Position of the donut chart
export const MODAL_ANIMATION_TIMEOUT: number = 300;
export const NAVBAR_SCROLL_TOP_DISTANCE: number = 250;
export const SMALL_DEVICES_WIDTH_THRESHOLD: number = 500;
export const MEDIUM_DEVICES_WIDTH_THRESHOLD: number = 768;
export const LARGE_DEVICES_WIDTH_THRESHOLD: number = 992;

// Providers
export const PROVIDER_SEPA: string = "sepa";
export const PROVIDER_SEPA_INSTANT: string = "sepa_instant"; // Sepa Instant
export const PROVIDER_BANK_TRANSFER: string = "bank_transfer"; // Non-Sepa bank
export const PROVIDER_HAVALE: string = "eft_havale"; // Turkish MultiBank - Non-european equivalent of Sepa
export const PROVIDER_AMAZON: string = "amazon";

// Locales -> for moment.js only!
export const LANGUAGE_ISO_CODES: Map<string, string> = new Map([
	["deu", "de"],
	["eng", "en"],
	["esp", "es"],
	["fra", "fr"],
	["ita", "it"],
	["spa", "es"],
	["pol", "pl"],
	["hrv", "hr"],
	["ron", "ro"],
	["hun", "hu"],
	["ces", "cs"],
	["nld", "nl"]
]);

// SocketRoom Names
export const INDEX_TRANSACTIONS_ROOM: string = "index_wallet_transactions";
export const INDEX_WALLETS_ROOM: string = "index_wallets";
export const CRYPTO_TRANSACTIONS_ROOM: string = "wallet_transactions";
export const CRYPTO_WALLETS_ROOM: string = "cryptocoin_wallets";
export const METAL_TRANSACTIONS_ROOM: string = "commodity_wallet_transactions";
export const METAL_WALLETS_ROOM: string = "commodity_wallets";
export const STOCK_TRANSACTIONS_ROOM: string = "stock_wallet_transactions";
export const STOCK_WALLETS_ROOM: string = "stock_wallets";
export const ETF_TRANSACTIONS_ROOM: string = "etf_wallet_transactions";
export const ETF_WALLETS_ROOM: string = "etf_wallets";
export const ETC_TRANSACTIONS_ROOM: string = "etc_wallet_transactions";
export const ETC_WALLETS_ROOM: string = "etc_wallets";
export const LEVERAGED_TOKEN_WALLETS_ROOM: string = "leveraged_token_wallets";
export const LEVERAGED_TOKEN_TRANSACTIONS_ROOM: string = "leveraged_token_transactions";
export const SECURITY_TOKEN_TRANSACTIONS_ROOM = "security_token_transactions";
export const SECURITY_TOKEN_WALLETS_ROOM: string = "security_wallets";

// Asset Types
export const CRYPTO_TYPE: string = "CRYPTO";
export const METAL_TYPE: string = "METAL";
export const INDEX_TYPE: string = "INDEX";
export const DERIVATIVE_TYPE: string = "SECURITY";
export const STOCK_TYPE: string = "STOCK";
export const ETF_TYPE: string = "ETF";
export const ETC_TYPE: string = "ETC";
export const LEVERAGED_TOKEN_TYPE: string = "LEVERAGED_TOKEN";
export const TRADABLE_TYPE: string = "TRADABLE";
export const FIAT_TYPE: string = "FIAT";
export const CURRENCY_TYPE: string = "CURRENCY";
export const COIN_TYPE: string = "COIN";
export const FIAT_EARN_TYPE: string = "FIAT_EARN";
export enum TRADABLES_TYPE {
	CRYPTO = "CRYPTO",
	METAL = "METAL",
	INDEX = "INDEX",
	STOCK = "STOCK",
	ETF = "ETF",
	ETC = "ETC",
	LEVERAGED_TOKEN = "LEVERAGED_TOKEN",
	FIAT_EARN = "FIAT_EARN",
	SECURITY_TOKEN = "SECURITY_TOKEN"
}

export const DEEP_LINK_FIAT_TYPE = "fiat";
export const DEEP_LINK_CRYPTO_TYPE = "crypto";
export const DEEP_LINK_LEVERAGED_TOKEN_TYPE = "leveraged_tokens";
export const DEEP_LINK_INDEX_TYPE = "indices";
export const DEEP_LINK_METAL_TYPE = "metals";
export const DEEP_LINK_STOCK_TYPE = "stocks";
export const DEEP_LINK_ETF_TYPE = "etfs";
export const DEEP_LINK_ETC_TYPE = "etcs";
export const DEEP_LINK_SECURITY_TYPE = "security_tokens";

export const deepLinkTypeToDomainTypeMap = {
	[DEEP_LINK_FIAT_TYPE]: FIAT_TYPE,
	[DEEP_LINK_CRYPTO_TYPE]: CRYPTO_TYPE,
	[DEEP_LINK_LEVERAGED_TOKEN_TYPE]: LEVERAGED_TOKEN_TYPE,
	[DEEP_LINK_INDEX_TYPE]: INDEX_TYPE,
	[DEEP_LINK_METAL_TYPE]: METAL_TYPE,
	[DEEP_LINK_STOCK_TYPE]: STOCK_TYPE,
	[DEEP_LINK_ETF_TYPE]: ETF_TYPE,
	[DEEP_LINK_ETC_TYPE]: ETC_TYPE
};

export const API_ALL_TYPES: string = "all";
export const API_CRYPTO_TYPE: string = "cryptocoin";
export const API_METAL_TYPE: string = "metal";
export const API_INDEX_TYPE: string = "index";
export const API_STOCK_TYPE: string = "stock";
export const API_ETF_TYPE: string = "etf";
export const API_ETC_TYPE: string = "etc";
export const API_LEVERAGED_TOKEN_TYPE: string = "leveraged_token";
export const API_SECURITY_TYPE: string = "security_token";

export const deepLinkTypeToApiTypeMap = {
	[DEEP_LINK_CRYPTO_TYPE]: API_CRYPTO_TYPE,
	[DEEP_LINK_LEVERAGED_TOKEN_TYPE]: API_LEVERAGED_TOKEN_TYPE,
	[DEEP_LINK_INDEX_TYPE]: API_INDEX_TYPE,
	[DEEP_LINK_METAL_TYPE]: API_METAL_TYPE,
	[DEEP_LINK_STOCK_TYPE]: API_STOCK_TYPE,
	[DEEP_LINK_ETF_TYPE]: API_ETF_TYPE,
	[DEEP_LINK_ETC_TYPE]: API_ETC_TYPE,
	[DEEP_LINK_SECURITY_TYPE]: API_SECURITY_TYPE
};

// Auth/Profile
export const TOP_COUNTRIES_LIST: string[] = ["CH", "FR", "GB", "AT", "DE"];
export const GERMANY_COUNTRY_ISO: string = "DE";
export const UK_COUNTRY_ISO: string = "GB";

// Stocks
export const APPROPRIATENESS_UNKNOWN: string = "appropriateness.unknown";
export const APPROPRIATENESS_APPROPRIATE: string = "appropriateness.appropriate";
export const APPROPRIATENESS_NOT_EXPERIENCED: string = "appropriateness.not_experienced";
export const APPROPRIATENESS_NOT_APPROPRIATE: string = "appropriateness.not_appropriate";
export const APPROPRIATENESS_REQUIRED_ERROR_CODE: string = "questionnaire.appropriateness_test.required";
export const APPROPRIATENESS_COOLDOWN_ERROR: string = "questionnaire.appropriateness_test.cooldown";
export const APPROPRIATENESS_COOLOFF_ERROR: string = "uk.questionnaire.appropriateness_test.transact.cooloff";
export const APPROPRIATENESS_BLOCKED_ERROR: string = "uk.questionnaire.appropriateness_test.user.blocked";

// Charts
export const CHART_UPDATE_INTERVAL: number = 900000; // Default chart update in every 15 minutes
export const NET_INCOME_CHART_DATA_LENGTH: number = 4;
export const OFF_EXCHANGE_HOURS_AMOUNT: number = 0;
export const MIN_PERCENTAGE_DISPLAY: number = 0.01;

// Featured Assets
export const FEATURED_ASSET_API_SLICE: number = 6;
export const FEATURED_ASSET_LIST_SLICE: number = 5;

// Country ID
export const TURKEY_COUNTRY_ID: string = "218";
export const SWEDEN_COUNTRY_ID: string = "205";
export const UK_COUNTRY_ID: string = "225";
export const COUNTRY_ID_AUSTRIA: string = "14";
export const GERMANY_COUNTRY_ID: string = "80";

// Offer
export const OFFER_MAX_REFRESHES: number = 4;
export const INFO_TOASTER_DURATION: number = 5000;
export const MULTI_SWAP_MAX_LENGTH: number = 10;

// Portfolio
export const UPDATE_PORTFOLIO_FILTER = "FIAT";
export const PORTFOLIO_API_TIMEOUT_PERIOD = 5000;
export const PORTFOLIO_MAX_ITEMS_TO_DISPLAY = 5;

// Typename
export const TYPE_NAME_FIAT = "FiatWallet";
export const TYPE_NAME_CRYPTO = "CryptoWallet";
export const TYPE_NAME_INDEX = "IndexWallet";
export const TYPE_NAME_STOCK = "StockWallet";
export const TYPE_NAME_ETF = "EtfWallet";
export const TYPE_NAME_ETC = "EtcWallet";
export const TYPE_NAME_LEVERAGED_TOKEN = "LeveragedTokenWallet";
export const TYPE_NAME_METAL = "MetalWallet";
export const TYPE_NAME_SECURITY = "SecurityTokenWallet";
export const TYPE_NAME_EQUITY_STOCK = "EquityStock";
export const TYPE_NAME_EQUITY_ETC = "EquityETC";
export const TYPE_NAME_EQUITY_ETF = "EquityETF";

// CurrencyPipe
export const LANGUAGES_WITH_PREFIXED_DENOMINATION: string[] = ["deu", "spa", "fra", "ita", "pol"];

// Google Places API
export const COUNTRY_RESTRICTIONS = 5;
export const GOOGLE_SEARCH_DEBOUNCE_TIME = 400;

// Transaction detail
export const SINGLE_TAG_MAX_LENGTH: number = 30;
export const TRADABLE_NAME_LENGTH_TAG: number = 15;

// BankID
export const APPSCHEME: string = "bankid://";
export const BANKID_APPLESTORE_LINK: string = "itms-apps://itunes.apple.com/app/id433151512";
export const BANKID_PLAYSTORE_LINK: string =
	"https://play.app.goo.gl/?link=https://play.google.com/store/apps/details?id=com.bankid.bus";
export const BANKID_DESKTOP_LINK: string = "https://install.bankid.com/";
export const MOBILE_AGENTS = /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone|Opera Mini|IEMobile|Mobile/i;
export const APPLE_AGENTS = /iPhone|iPad|iPod/i;

// Pagination
export const PAGINATION_PAGE_SIZES: number[] = [10, 25, 50];

// Favourites
export const FOLLOWING_WIDGET_MAX_ITEMS: number = 5;
export const ADP_LIMIT_ORDER_SIZE: number = 3;
export const WATCHLIST_API_MAX_SIZE: number = 500;
export const WATCHLIST_CHECK_MARK_FILLED: string = "/assets/images/check-mark-filled.svg";
export const WATCHLIST_CHECK_MARK_EMPTY: string = "/assets/images/check-mark-empty.svg";
export const ASSET_FAVORITED: string = "Asset Favorited";
export const ASSET_UNFAVORITED: string = "Asset Unfavorited";

// Modals
export const CASH_PLUS = "cash-plus";
export const TAXES = "taxes";
export const TIN = "tin";
export const CHANGE_USER_INFO = "user-info";
export const DUST_TO_BEST = "dust-to-best";
export const SELECT_ASSETS = "select-assets";
export const CRYPTO_DEPOSIT_VALIDATION = "crypto-deposit-validation";

export const TAXES_NOT_ELIGIBLE_COOKIE_KEY = "taxes_not_eligible";

// Generic Confirm Modal translation keys
export const CONFIRM_MODAL_TITLE: string = "close_window?";
export const CONFIRM_MODAL_CANCEL: string = "cancel";
export const CONFIRM_MODAL_OK: string = "ok";

// Min-Modal height without content
export const MIN_MODAL_HEIGHT: number = 150;

// Dismiss modal (background click) translation keys
export const DISMISS_MODAL_TITLE: string = "close_window?";
export const DISMISS_MODAL_MESSAGE: string = "unsaved_data_will_get_lost";
export const DISMISS_MODAL_CANCEL: string = "modal_confirm_no_option";
export const DISMISS_MODAL_OK: string = "modal_confirm_yes_option";

// Modal routes
export const VERIFICATION_MODAL_ROUTE: { outlets: { modal: string[] } }[] = [{ outlets: { modal: ["verification"] } }];

// Prices
export const PRICES_SCROLL_PER_PAGE: number = 20;
export const PRICES_SCROLL_PER_PAGE_THRESHOLD: number = 5; // Used when API sends back less than expected assets per page, so we know if it makes sense to fetch next page or not

export const DEBIT_CARD_PLAYSTORE_URL = config.externalPaths["mobileApps"]["androidUrl"];
export const DEBIT_CARD_APPSTORE_URL = config.externalPaths["mobileApps"]["iosUrl"];

// Referrals
export const REFERRAL_COOKIE = "ref";
export const REFERRAL_INFO_COOKIE = "ref_info";
export const SOCIAL_TRADING_REFERRAL_TYPE = "SOCIAL_TRADING";
export const TWO_DAYS_EXPIRY = 2 * 86400000;
export const ONE_HOUR_EXPIRY = 86400000 / 24;
export const SHARE_TRANSACTION_MAX_MONTH_LIMIT: number = 11;

// IOS
export const IOS_BUNDLE_ID = config.externalPaths["mobileApps"]["bundleId"];
export const IOS_APP_STORE_URL = config.externalPaths["mobileApps"]["iosUrl"];

// Android
export const ANDROID_BUNDLE_ID = config.externalPaths["mobileApps"]["bundleId"];
export const ANDROID_MIN_VERSION_CODE = "22300000";
export const ANDROID_PLAY_STORE_URL = config.externalPaths["mobileApps"]["androidUrl"];

export const NO_DEPOSITS_VALUE = 0;

// Navigator.platform consts
export const APPLE_MOBILE_PLATFORMS = [
	"iPad Simulator",
	"iPhone Simulator",
	"iPod Simulator",
	"iPad",
	"iPhone",
	"iPod"
];
export const IOS_VERSION_FOR_NEW_REDIRECTION: number = 2.25;

// Rudderstack and Segment

export const FIRST_TRADE_EXECUTED = "Trade First Completed";
export const PRICES_CURRENCY_FILTER_CLICKED_EVENT = "Web - Prices Page - Clicked - Currency Filter - Option";
export const PRICES_TIMEFRAME_CLICKED_EVENT = "Web - Prices Page - Clicked - Time Frame Selector - Option";
export const KYC_HOME_REVIEW_BUTTON_CLICK_EVENT = "Web - KYC - Clicked - Home banner - Review button";
export const KYC_REVIEW_POPUP_DEEPLINK_VIEWED = "Web - KYC - Viewed - Review popup - Deep link";
export const KYC_EDIT_DETAILS_TOGGLE_VIEWED = "Web - KYC - Viewed - Edit details - Toggle visible";
export const KYC_EDIT_DETAILS_NO_TOGGLE_VIEWED = "Web - KYC - Viewed - Edit details - Toggle not visible";
export const KYC_EDIT_DETAILS_SUPPORT_FORM_SELECTED = "Web - KYC - Selected - Edit details - Support form";
export const KYC_EDIT_DETAILS_CANCEL_BUTTON_CLICK_EVENT = "Web - KYC - Clicked - Edit details - Cancel process";
export const KYC_EDIT_DETAILS_CONFIRM_BUTTON_CLICK_EVENT = "Web - KYC - Confirmed - Edit details - Confirm button";
export const KYC_EDIT_DETAILS_CLOSE_BUTTON_CLICK_EVENT = "Web - KYC - Clicked - Edit details - Close button";
export const KYC_EDIT_DETAILS_DEEPLINK_VIEWED = "Web - KYC - Viewed - Edit details - Deep link";
export const KYC_SUCCESS_SCREEN_VIEWED = "Web - KYC - Viewed - Success screen";
export const KYC_SUCCESS_CLOSE_BUTTON_CLICK_EVENT = "Web - KYC - Clicked - Success screen - Finished";
export const KYC_SUCCESS_BACK_BUTTON_CLICK_EVENT = "Web - KYC - Clicked - Success screen - Back to edit";
export const KYC_PROVIDER_SUMSUB = "sumsub";
export const KYC_PROVIDER_SUMSUBVIDEO = "sumsubvideo";
export const KYC_PROVIDER_ONFIDO = "onfido";
export const SEGMENT_PROPERTIES: Dictionary<string> = {
	day: "1D",
	week: "1W",
	month: "1M",
	"six-months": "6M",
	year: "1Y",
	"five-years": "5Y"
};

// Leveraged token
export const LEVERAGED_TOKEN_LONG_ICON_VALUE = "2↑";
export const LEVERAGED_TOKEN_SHORT_ICON_VALUE = "1↓";

// Cash Plus
export const CASH_PLUS_ROLL_HOUR_EURO_TIME = "10:00 CET";
export const CASH_PLUS_ROLL_HOUR_USD_TIME = "14:00 CET";
export const CONTENT_TYPE_OCTET_STREAM = "application/octet-stream";

export const BREAKPOINT_SCREEN_SM: string = "(max-width: 767px)";
export const BREAKPOINT_SCREEN_MD: string = "(min-width: 768px) and (max-width: 991px)";
export const BREAKPOINT_SCREEN_LG: string = "(min-width: 992px)";

// Storage Period in Days for Banners
export const BANNER_CAROUSEL_STORAGE_DAYS: number = 30;
export const BANNER_TAXES_STORAGE_DAYS: number = 7;
// Limit order
export const PRICE_ORDER_INFO_COOKIE_KEY = "seenPriceOrderInfo";
export const PRICE_ORDER_INFO_COOKIE_KEY_SELL = "seenPriceOrderInfoSell";
export const PRICE_ORDER_NEW_LABEL = "seenPriceOrderOption";
export const LIMIT_ORDER_INTRO_SCREEN_SEEN = "limitOrderIntroScreenSeen";
// Start with 0, so it's one more => 3
export const LIMIT_ORDER_INFO_TOOLTIP_COUNTER = 2;
// for switcher visibility variations
export const LIMIT_ORDERS_SWITCH_VIEWED = "limitOrdersSwitchViewed";

// Advanced trading
export const ADVANCED_TRADING_MODAL_SEEN = "advancedTradingModalSeen";
export const ADVANCED_TRADING_LOGIN = "a_t_l";
export const ADVANCED_TRADING_TOOLTIP_CLICKED = "advancedTradingTooltipClicked";

export const AUTO_STAKING_OVERVIEW_WIDGET_DISMISSED = "autoStakingOverviewWidgetDismissed";
export const AUTO_STAKING_INTRO_SEEN = "autoStakingIntroSeen";
export const EARN_ON_STABLECOIN_INTRO_SEEN = "earnOnStakingIntroSeen";

//Customer Segmentation
export const CUSTOMER_SEGMENTATION_LABEL = "uk_customer_segmentation_appropriateness_test";
export const APPROPRIATENESS_WITH_SETS = "uk_crypto_appropriateness_test_with_sets";
export const RESTRICTED_INVESTOR_FLOW = 1;
export const HIGH_NET_INVESTOR_FLOW = 2;
export const NOT_AN_INVESTOR_FLOW = 3;

export const INVESTOR_PROFILE_QUESTION = "uk_customer_segmentation_1_general";
export const RESTRICTED_INVESTOR_STATEMENT = "uk_customer_segmentation_2_restricted_investor_statement";
export const RESTRICTED_INVESTOR_PAST_QUESTION_ONE =
	"uk_customer_segmentation_3_restricted_investor_past_twelve_invested";
export const RESTRICTED_INVESTOR_PAST_QUESTION_TWO =
	"uk_customer_segmentation_4_restricted_investor_past_twelve_invested_if_yes";
export const RESTRICTED_INVESTOR_FUTURE_QUESTION_ONE =
	"uk_customer_segmentation_5_restricted_investor_past_twelve_limit_investment";
export const RESTRICTED_INVESTOR_FUTURE_QUESTION_TWO =
	"uk_customer_segmentation_6_restricted_investor_past_twelve_limit_investment_if_yes";
export const RESTRICTED_INVESTOR_CONFIRMATION = "uk_customer_segmentation_7_restricted_investor_agreement";
export const HIGH_NET_INVESTOR_QUESTION_ONE = "uk_customer_segmentation_8_high_net_worth_investor_annual_income";
export const HIGH_NET_INVESTOR_QUESTION_TWO = "uk_customer_segmentation_9_high_net_worth_investor_annual_income_if_yes";
export const HIGH_NET_INVESTOR_QUESTION_THREE = "uk_customer_segmentation_10_high_net_worth_investor_net_assets";
export const HIGH_NET_INVESTOR_QUESTION_FOUR = "uk_customer_segmentation_11_high_net_worth_investor_net_assets_if_yes";
export const HIGH_NET_INVESTOR_QUESTION_SIX = "uk_customer_segmentation_13_high_net_worth_investor_agreement";
export const END_CRYPTO_JOURNEY = "uk_customer_segmentation_14_neither";
export const RESTRICTED_INVESTOR_ANSWER = "uk_customer_segmentation_1_general_restricted_investor";
export const HIGH_NET_INVESTOR_ANSWER = "uk_customer_segmentation_1_general_high_net_worth_investor";
export const NEITHER_ANSWER = "uk_customer_segmentation_1_general_neither";

// Margin Trading
export const MARGIN_TRADING_INTRO_SEEN = "marginTradingIntroSeen";

//ALL STOCK
export const SECURITY_ASSET_GROUP_LIST = ["", API_STOCK_TYPE, API_ETF_TYPE, API_ETC_TYPE];

//REAL STOCK
export const PILLS_THAT_NEEDS_SECURITY_BANNER = ["", API_STOCK_TYPE, API_ETF_TYPE, API_ETC_TYPE];
export const DISCOVERY_PILLS_THAT_NEEDS_SECURITY_BANNER = [
	"",
	API_ALL_TYPES,
	API_STOCK_TYPE,
	API_ETF_TYPE,
	API_ETC_TYPE
];
export const DISCOVERY_PILLS_THAT_NEEDS_CONTENT = [
	"",
	API_ALL_TYPES,
	API_CRYPTO_TYPE,
	API_METAL_TYPE,
	API_INDEX_TYPE,
	API_LEVERAGED_TOKEN_TYPE,
	API_SECURITY_TYPE
];

export const ALL_FILTERS_AVAILABLE = [
	API_CRYPTO_TYPE,
	API_INDEX_TYPE,
	API_METAL_TYPE,
	API_LEVERAGED_TOKEN_TYPE,
	API_SECURITY_TYPE
];
// Excluded assets
export const EXCLUDED_ASSET_SYMBOLS = [
	"KMD",
	"MIOTA",
	"REP",
	"ATOM",
	"XMR",
	"XDB",
	"SRM",
	"XRT",
	"FLR",
	"CTXC",
	"AZERO",
	"MDX",
	"FOR",
	"COMBO",
	"IGU",
	"DOGA",
	"CAGA",
	"LBR",
	"INFRA",
	"FCON",
	"VIA",
	"PDA",
	"NULS",
	"O4DX",
	"LYNX",
	"EXVG",
	"FRBK",
	"BLAZE",
	"LOE",
	"VEXT",
	"BEFI",
	"ORFY",
	"WOLF",
	"LAND",
	"EMT",
	"NOTAI",
	"UNIT0",
	"GODL",
	"EYWA",
	"AIAGENT",
	"KIP",
	"DESCI",
	"HAI",
	"GPT",
	"ONDOAI",
	"IOTA",
	"PUMP",
	"ASTER"
];
